/**
 * RevenueSection Component
 *
 * Displays revenue analytics including stat cards, bar chart for monthly trends,
 * and pie chart for revenue by service breakdown.
 */

import { Box, Typography, Grid, Paper } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import StatCard from './StatCard';
import EmptyState from './EmptyState';
import { SectionSkeleton } from './AnalyticsSkeleton';
import { ANALYTICS_SPACING } from './constants';
import { RevenueStats } from '../../services/analyticsApi';

interface RevenueSectionProps {
  data: RevenueStats | null;
  loading?: boolean;
}

// Colors for pie chart segments
const PIE_COLORS = [
  '#5D5FEF', // Elevair purple
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#84CC16', // Lime
  '#F97316', // Orange
];

/**
 * Format currency for tooltip (cents to dollars)
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value / 100);
}

/**
 * Custom tooltip for bar chart
 */
function BarChartTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}) {
  if (active && payload && payload.length && payload[0]) {
    return (
      <Paper sx={{ p: 1.5, boxShadow: 2 }}>
        <Typography variant="body2" fontWeight={500}>
          {label}
        </Typography>
        <Typography variant="body2" color="primary">
          {formatCurrency(payload[0].value)}
        </Typography>
      </Paper>
    );
  }
  return null;
}

/**
 * Custom tooltip for pie chart
 */
function PieChartTooltip({ active, payload }: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; payload: { percentage: number } }>;
}) {
  if (active && payload && payload.length && payload[0]) {
    const data = payload[0];
    return (
      <Paper sx={{ p: 1.5, boxShadow: 2 }}>
        <Typography variant="body2" fontWeight={500}>
          {data.name}
        </Typography>
        <Typography variant="body2" color="primary">
          {formatCurrency(data.value)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {data.payload?.percentage ?? 0}% of total
        </Typography>
      </Paper>
    );
  }
  return null;
}

export default function RevenueSection({ data, loading = false }: RevenueSectionProps) {
  // Show skeleton while loading
  if (loading) {
    return <SectionSkeleton />;
  }

  // Check for empty data state
  const isEmpty =
    !data || (data.summary?.total === 0 && data.summary?.invoicesPaid === 0);

  if (isEmpty) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={600} sx={{ mb: ANALYTICS_SPACING.titleMarginBottom }}>
          Revenue
        </Typography>
        <EmptyState type="revenue" />
      </Box>
    );
  }

  // Transform chart data for Recharts (shorten month labels)
  const chartData =
    data?.chartData?.map((item) => ({
      month: item.month.split(' ')[0], // "Jan 2026" -> "Jan"
      revenue: item.revenue,
    })) || [];

  // Transform service data for pie chart
  const serviceData =
    data?.byService?.map((item) => ({
      name: item.service,
      value: item.revenue,
      percentage: item.percentage,
    })) || [];

  // Build accessible description for revenue trend chart
  const trendChartDescription =
    chartData.length > 0
      ? `Revenue trend chart showing ${chartData.length} months. ${chartData
          .map((d) => `${d.month}: ${formatCurrency(d.revenue)}`)
          .join(', ')}`
      : 'No revenue trend data available';

  // Build accessible description for service breakdown chart
  const serviceChartDescription =
    serviceData.length > 0
      ? `Revenue by service breakdown. ${serviceData
          .map((s) => `${s.name}: ${formatCurrency(s.value)} (${s.percentage}%)`)
          .join(', ')}`
      : 'No service data available';

  return (
    <Box>
      {/* Section Title */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: ANALYTICS_SPACING.titleMarginBottom }}>
        Revenue
      </Typography>

      {/* Stat Cards Grid */}
      <Grid container spacing={ANALYTICS_SPACING.cardGap} sx={{ mb: ANALYTICS_SPACING.cardsToChartsGap }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Revenue"
            value={data?.summary?.total || 0}
            format="currency"
            trend={data?.summary?.changePercent}
            subtitle="vs previous period"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Outstanding"
            value={data?.summary?.outstandingAmount || 0}
            format="currency"
            subtitle={`${data?.summary?.invoicesOutstanding || 0} invoices`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Avg Invoice"
            value={data?.summary?.averageInvoiceValue || 0}
            format="currency"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Paid Invoices"
            value={data?.summary?.invoicesPaid || 0}
            format="number"
            subtitle="this period"
          />
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={ANALYTICS_SPACING.chartGap}>
        {/* Revenue Trend Bar Chart */}
        <Grid item xs={12} lg={7}>
          <Paper
            elevation={0}
            sx={{
              p: ANALYTICS_SPACING.sectionPadding,
              height: '100%',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" fontWeight={500} sx={{ mb: 3 }}>
              Revenue Trend
            </Typography>
            {/* Screen reader description */}
            <Box component="span" sx={visuallyHidden}>
              {trendChartDescription}
            </Box>
            <Box sx={{ width: '100%', height: 300 }} aria-hidden="true">
              <ResponsiveContainer>
                <BarChart
                  data={chartData}
                  margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={{ stroke: '#E5E7EB' }}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={{ stroke: '#E5E7EB' }}
                    tickFormatter={(value) => `$${(value / 100000).toFixed(0)}k`}
                  />
                  <Tooltip content={<BarChartTooltip />} />
                  <Bar
                    dataKey="revenue"
                    fill="#5D5FEF"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={50}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Revenue by Service Pie Chart */}
        <Grid item xs={12} lg={5}>
          <Paper
            elevation={0}
            sx={{
              p: ANALYTICS_SPACING.sectionPadding,
              height: '100%',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" fontWeight={500} sx={{ mb: 3 }}>
              Revenue by Service
            </Typography>
            {/* Screen reader description */}
            <Box component="span" sx={visuallyHidden}>
              {serviceChartDescription}
            </Box>
            <Box sx={{ width: '100%', height: 300 }} aria-hidden="true">
              {serviceData.length > 0 ? (
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={serviceData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                    >
                      {serviceData.map((_, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={PIE_COLORS[index % PIE_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<PieChartTooltip />} />
                    <Legend
                      layout="vertical"
                      align="right"
                      verticalAlign="middle"
                      formatter={(value) => (
                        <span style={{ fontSize: 12, color: '#374151' }}>
                          {value.length > 20 ? `${value.substring(0, 20)}...` : value}
                        </span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                >
                  <Typography color="text.secondary">No service data available</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
